import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { API_ENDPOINTS, getFileUrl } from '../config/api';

interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  media: Array<{
    id: string;
    type: 'image' | 'video';
    url: string;
    filename: string;
    isPrimary?: boolean;
  }>;
  category: string;
  featured: boolean;
  order: number;
  createdAt: string;
}

// Media carousel component for individual portfolio items
const MediaCarousel: React.FC<{ media: PortfolioItem['media']; title: string }> = ({ media, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  if (!media || media.length === 0) {
    return (
      <Box
        sx={{
          height: 250,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.200',
          borderRadius: '16px 16px 0 0',
        }}
      >
        <Typography color="text.secondary">No media</Typography>
      </Box>
    );
  }

  const currentMedia = media[currentIndex];

  return (
    <Box sx={{ position: 'relative', height: 250 }}>
      {currentMedia.type === 'video' ? (
        <CardMedia
          component="video"
          height="250"
          src={getFileUrl(currentMedia.url)}
          controls
          sx={{ borderRadius: '16px 16px 0 0', objectFit: 'cover' }}
        />
      ) : (
        <CardMedia
          component="img"
          height="250"
          image={getFileUrl(currentMedia.url)}
          alt={title}
          sx={{ borderRadius: '16px 16px 0 0', objectFit: 'cover' }}
        />
      )}

      {/* Navigation arrows - only show if more than 1 media item */}
      {media.length > 1 && (
        <>
          <IconButton
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
            }}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
            }}
          >
            <ChevronRight />
          </IconButton>

          {/* Indicator dots */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
            }}
          >
            {media.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: index === currentIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(index);
                }}
              />
            ))}
          </Box>
        </>
      )}

      {/* Media counter */}
      {media.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            fontSize: '0.875rem',
          }}
        >
          {currentIndex + 1} / {media.length}
        </Box>
      )}
    </Box>
  );
};

const Portfolio: React.FC = () => {
  const theme = useTheme();
  const { t } = useLanguage();
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPortfolioItems = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.PORTFOLIO);
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            // Sort by order, then by creation date
            const sorted = (data.items || []).sort((a: PortfolioItem, b: PortfolioItem) => {
              if (a.order !== b.order) return a.order - b.order;
              return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            });
            setPortfolioItems(sorted);
          }
        }
      } catch (error) {
        console.error('Error loading portfolio items:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPortfolioItems();
  }, []);

  return (
    <Box sx={{ py: 8 }}>
      <Paper
        elevation={0}
        sx={{
          p: 6,
          textAlign: 'center',
          background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}15 100%)`,
          borderRadius: 4,
          mb: 8,
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          color="primary"
          sx={{ fontWeight: 700 }}
        >
          {t('portfolio.title')}
        </Typography>
        <Typography
          variant="h5"
          color="text.secondary"
          paragraph
          sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}
        >
          {t('portfolio.subtitle')}
        </Typography>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : portfolioItems.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 4,
          }}
        >
          {portfolioItems.map((item, index) => (
            <Card
              key={item.id}
              elevation={0}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.light}10 0%, ${theme.palette.secondary.light}10 100%)`,
                borderRadius: 4,
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  background: `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.secondary.light}20 100%)`,
                },
              }}
            >
              <MediaCarousel media={item.media} title={item.title} />
              <CardContent>
                <Typography
                  variant="h5"
                  component="h2"
                  gutterBottom
                  color="primary"
                  sx={{ fontWeight: 600 }}
                >
                  {item.title}
                </Typography>
                <Typography color="text.secondary">
                  {item.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 4,
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No portfolio items available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later for new projects and designs
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Portfolio; 